import type { SortFilterItem } from '../Autocomplete/types';

const collator = new Intl.Collator();

/**
 * Sorts a list of SortFilterItem items with text-based search prioritization.
 * Prioritizes:
 * 1. Items that start with the search text
 * 2. Items with matches earlier in the string
 * 3. Alphabetical order
 */
export function sortSearchResults<T>(
  items: SortFilterItem<T>[],
  searchText: string,
  getSearchString: (item: SortFilterItem<T>) => string
): SortFilterItem<T>[] {
  const search = searchText.toLowerCase();
  const keyed = items.map(item => {
    const key = getSearchString(item).toLowerCase();
    return { item, key, index: key.indexOf(search) };
  });

  const sortedKeyed = keyed.toSorted((a, b) => {
    const startWithA = a.index === 0;
    const startWithB = b.index === 0;
    if (startWithA !== startWithB) {
      return startWithA ? -1 : 1;
    }

    if (a.index !== b.index) {
      if (a.index === -1) {
        return 1;
      }
      if (b.index === -1) {
        return -1;
      }
      return a.index - b.index;
    }

    return collator.compare(a.key, b.key);
  });

  return sortedKeyed.map(({ item }) => item);
}

/**
 * Creates a sortFilterOverride function that provides custom search filtering and sorting.
 * Filters by substring match, then sorts prioritizing exact matches and match position.
 *
 * @param getSearchString - Function to extract the searchable string from a SortFilterItem
 */
export function createCustomSortFilter<T>(getSearchString: (item: SortFilterItem<T>) => string) {
  return (fullList: SortFilterItem<T>[], searchText: string): SortFilterItem<T>[] => {
    const lowerSearch = searchText.toLowerCase();
    const filteredList = fullList.filter(item => getSearchString(item).toLowerCase().includes(lowerSearch));

    return sortSearchResults(filteredList, searchText, getSearchString);
  };
}
